// The activity record, work entries and timesheets (SPEC-v2 W1-W6;
// API.md 7.2, endpoints 49-53). The day rule itself is in ../timesheet.ts.
import { z } from "zod";
import {
  CONTRIBUTION_ROLES,
  type ChecklistKey,
  type ContractStatus,
  type ContributionRole,
  type DeliverableKind,
  type DeliverableStatus,
  type ExpertCategory,
  type RunDaySkipReason,
  type WorkSource,
} from "../enums";
import { LIMITS } from "../limits";
import { type ActorRef, cents, isoDate, isoMonth, optionalText, uuid } from "./common";
import type { EventSummary } from "./events";
import type { TorDetail } from "./tors";
import type { TorDetailV2Fields } from "./v1-additions";

// ---------- Building blocks ----------

/** Hours worked: 0.5 to 24, in steps of 0.5. */
export const workHours = z
  .number({ invalid_type_error: "Enter the hours." })
  .min(LIMITS.workHoursMin, "At least half an hour.")
  .max(LIMITS.workHoursMax, "At most 24 hours.")
  .refine((n) => Number.isInteger(n * 2), "Use steps of half an hour (0.5).");

/** One contributor: exactly one of contactId / stakeholderOrgId, with a role. */
export const contributorInput = z
  .object({
    contactId: uuid.optional(),
    stakeholderOrgId: uuid.optional(),
    role: z.enum(CONTRIBUTION_ROLES),
  })
  .strict()
  .superRefine((v, ctx) => {
    if ((v.contactId === undefined) === (v.stakeholderOrgId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contactId"],
        message: "Choose a person or an organisation.",
      });
    }
  });
export type ContributorInput = z.infer<typeof contributorInput>;

export const contributorsInput = z
  .array(contributorInput)
  .max(LIMITS.contributorsMax, `At most ${LIMITS.contributorsMax} contributors.`);

// ---------- Requests ----------

/**
 * 49. PATCH /api/tors/:id/record. All fields optional (`{}` answers
 * EMPTY_PATCH). `contributors` replaces the whole list.
 */
export const updateTorRecordBody = z
  .object({
    debrief: optionalText(LIMITS.debrief, "Debrief").optional(),
    actualCents: cents(LIMITS.actualCentsMax).nullable().optional(),
    resultId: uuid.nullable().optional(),
    contributors: contributorsInput.optional(),
  })
  .strict();
export type UpdateTorRecordBody = z.infer<typeof updateTorRecordBody>;

/** 50. POST /api/tors/:id/run-days */
export const runDaysBody = z
  .object({
    contractExpertIds: z
      .array(uuid)
      .min(1, "Choose at least one expert.")
      .max(LIMITS.runDayExpertsMax, `At most ${LIMITS.runDayExpertsMax} experts at a time.`),
    /** default: every date of the ToR (start to end) */
    dates: z
      .array(isoDate)
      .min(1, "Choose at least one date.")
      .max(LIMITS.runDayDatesMax, `At most ${LIMITS.runDayDatesMax} dates.`)
      .optional(),
    hours: workHours.default(7),
  })
  .strict();
export type RunDaysBody = z.input<typeof runDaysBody>;
export type RunDaysInput = z.output<typeof runDaysBody>;

/** 51. GET /api/timesheets?contractId&month */
export const timesheetQuery = z.object({ contractId: uuid, month: isoMonth }).strict();
export type TimesheetQuery = z.infer<typeof timesheetQuery>;

/** Path of 52 and 53: /api/timesheets/:contractExpertId/:month (a malformed one answers 404). */
export const timesheetParams = z.object({ contractExpertId: uuid, month: isoMonth });
export type TimesheetParams = z.infer<typeof timesheetParams>;

export const workEntryInput = z
  .object({
    /** an existing entry of this engagement and month keeps its id and source */
    id: uuid.optional(),
    torId: uuid,
    date: isoDate,
    hours: workHours,
    note: optionalText(LIMITS.workNote, "Note").default(""),
  })
  .strict();
export type WorkEntryInput = z.output<typeof workEntryInput>;

/**
 * 52. PUT /api/timesheets/:contractExpertId/:month. Replaces all of the
 * engagement's entries in that month. The API also checks that each date is
 * in :month and that no date adds up to more than 24 hours
 * (`fieldErrors.entries`); `timesheetEntriesProblems` does the pure part.
 */
export const putTimesheetBody = z
  .object({
    entries: z
      .array(workEntryInput)
      .max(LIMITS.timesheetEntriesMax, `At most ${LIMITS.timesheetEntriesMax} entries in a month.`),
  })
  .strict();
export type PutTimesheetBody = z.input<typeof putTimesheetBody>;
export type PutTimesheetInput = z.output<typeof putTimesheetBody>;

/** The month and 24-hour checks of endpoint 52, shared by the API and the timesheet grid. */
export function timesheetEntriesProblems(month: string, entries: readonly { date: string; hours: number }[]): string[] {
  const problems: string[] = [];
  const perDate = new Map<string, number>();
  for (const e of entries) {
    if (e.date.slice(0, 7) !== month) problems.push(`${e.date} is not in ${month}.`);
    perDate.set(e.date, (perDate.get(e.date) ?? 0) + e.hours);
  }
  for (const [date, hours] of [...perDate.entries()].sort()) {
    if (hours > LIMITS.workHoursMax) problems.push(`${date} adds up to ${hours} hours. The most is 24.`);
  }
  return problems;
}

/** 53. POST /api/timesheets/:contractExpertId/:month/approval */
export const timesheetApprovalBody = z.object({ approved: z.boolean() }).strict();
export type TimesheetApprovalBody = z.infer<typeof timesheetApprovalBody>;

// ---------- Responses ----------

export type WorkEntry = {
  id: string;
  contractExpertId: string;
  torId: string;
  activityCode: string;
  torTitle: string;
  date: string;
  /** multiple of 0.5, 0.5..24 */
  hours: number;
  note: string;
  source: WorkSource;
  createdAt: string;
  updatedAt: string;
};

/** One row per date with entries. */
export type TimesheetDay = { date: string; hours: number; dayCredit: 0 | 0.5 | 1 };

export type TimesheetMonth = {
  contractExpertId: string;
  poolExpertId: string;
  name: string;
  category: ExpertCategory;
  positionTitle: string;
  feeRateCents: number | null;
  /** "YYYY-MM" */
  month: string;
  /** by date, then createdAt */
  entries: WorkEntry[];
  /** by date */
  days: TimesheetDay[];
  /** valueCents null when no fee */
  totals: { hours: number; days: number; valueCents: number | null };
  approval: { approvedAt: string; approvedBy: ActorRef } | null;
  can: { edit: boolean; approve: boolean; reopen: boolean };
};

/** 51. GET /api/timesheets */
export type TimesheetsResponse = {
  contract: { id: string; name: string; reference: string; status: ContractStatus; isLead: boolean };
  month: string;
  /** approved ToRs of the contract: the picker for new entries */
  activities: { torId: string; activityCode: string; title: string; startDate: string | null; endDate: string | null }[];
  /** every engagement of the contract, by name */
  experts: TimesheetMonth[];
};

/** 50. POST /api/tors/:id/run-days */
export type RunDaysResponse = {
  created: number;
  skipped: { contractExpertId: string; date: string; reason: RunDaySkipReason }[];
  tor: TorDetail & TorDetailV2Fields;
};

// ----- the activity record (on TorDetail) -----

export type Contributor = {
  id: string;
  role: ContributionRole;
  contact: { id: string; name: string } | null;
  stakeholderOrg: { id: string; name: string } | null;
};

export type ChecklistItem = { key: ChecklistKey; label: string; done: boolean; applicable: boolean };

export type ActivityRecord = {
  debrief: string;
  actualCents: number | null;
  checklist: ChecklistItem[];
  /** applicable items only */
  progress: { done: number; total: number };
  experts: {
    contractExpertId: string;
    poolExpertId: string;
    name: string;
    positionTitle: string;
    plannedDays: number;
    loggedHours: number;
    aboutDays: number;
  }[];
  /** newest 50 on this ToR */
  recentWork: WorkEntry[];
  event: EventSummary | null;
  deliverables: { id: string; title: string; kind: DeliverableKind; status: DeliverableStatus }[];
  contributors: Contributor[];
  can: { edit: boolean; logRunDays: boolean };
};

